import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  useColorModeValue,
  Spinner,
  Center,
} from '@chakra-ui/react';
import NotificationsApi from 'api/notifications';
import { useNotifications } from 'contexts/NotificationsContext';

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const bgColor = useColorModeValue('white', 'navy.800');
  const { refreshUnreadCount } = useNotifications();

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      console.log('[Notifications] Fetching notifications...');
      const items = await NotificationsApi.getNotifications();
      console.log('[Notifications] Received notifications:', items);
      setNotifications(items || []);
    } catch (error) {
      console.error('[Notifications] Failed to fetch notifications', error);
      console.error('[Notifications] Error details:', error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await NotificationsApi.markAsRead(id);
      await fetchNotifications();
      refreshUnreadCount();
    } catch (error) {
      console.error('Failed to mark notification as read', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      const unreadNotifications = notifications.filter((n) => !n.isRead);
      await Promise.all(
        unreadNotifications.map((n) => NotificationsApi.markAsRead(n.id))
      );
      await fetchNotifications();
      refreshUnreadCount();
    } catch (error) {
      console.error('Failed to mark all notifications as read', error);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch (e) {
      return dateString;
    }
  };

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Flex direction="column" gap="20px">
        <Flex justify="space-between" align="center">
          <Text fontSize="2xl" fontWeight="bold" color={textColor}>
            התראות
          </Text>
          {unreadCount > 0 && (
            <Button colorScheme="blue" size="sm" onClick={handleMarkAllRead}>
              סמן הכל כנקרא ({unreadCount})
            </Button>
          )}
        </Flex>

        {loading ? (
          <Center py="40px">
            <Spinner size="xl" />
          </Center>
        ) : notifications.length === 0 ? (
          <Box
            p="40px"
            bg={bgColor}
            borderRadius="15px"
            border="1px solid"
            borderColor={borderColor}
          >
            <Text textAlign="center" color={textColor}>
              אין התראות
            </Text>
          </Box>
        ) : (
          <Box
            bg={bgColor}
            borderRadius="15px"
            border="1px solid"
            borderColor={borderColor}
            overflow="hidden"
          >
            <Table variant="simple" colorScheme="gray">
              <Thead>
                <Tr>
                  <Th color={textColor}>סטטוס</Th>
                  <Th color={textColor}>כותרת</Th>
                  <Th color={textColor}>הודעה</Th>
                  <Th color={textColor}>תאריך</Th>
                  <Th color={textColor}>פעולות</Th>
                </Tr>
              </Thead>
              <Tbody>
                {notifications.map((notification) => (
                  <Tr
                    key={notification.id}
                    bg={!notification.isRead ? 'blue.50' : 'transparent'}
                    _dark={{ bg: !notification.isRead ? 'blue.900' : 'transparent' }}
                  >
                    <Td>
                      {!notification.isRead ? (
                        <Badge colorScheme="red">לא נקרא</Badge>
                      ) : (
                        <Badge colorScheme="green">נקרא</Badge>
                      )}
                    </Td>
                    <Td color={textColor} fontWeight={!notification.isRead ? 'bold' : 'normal'}>
                      {notification.title}
                    </Td>
                    <Td color={textColor}>{notification.message}</Td>
                    <Td color={textColor}>{formatDate(notification.createdAt)}</Td>
                    <Td>
                      {!notification.isRead && (
                        <Button
                          size="xs"
                          colorScheme="blue"
                          onClick={() => handleMarkAsRead(notification.id)}
                        >
                          סמן כנקרא
                        </Button>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </Flex>
    </Box>
  );
}
